
import React, { useState, useEffect } from 'react';
import { CreditCard } from '../types';
import { maskCurrency, parseCurrencyToNumber } from '../utils/helpers';

interface CardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (card: CreditCard) => void;
  initialData?: CreditCard;
}

const CardModal: React.FC<CardModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [name, setName] = useState('');
  const [limit, setLimit] = useState('0,00');
  const [closingDate, setClosingDate] = useState('5');
  const [dueDate, setDueDate] = useState('10');

  // Reset or set fields when opening modal
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setName(initialData.name);
        setLimit(maskCurrency(initialData.limit));
        setClosingDate(String(initialData.closingDate));
        setDueDate(String(initialData.dueDate || 10));
      } else {
        setName('');
        setLimit('0,00');
        setClosingDate('5');
        setDueDate('10');
      }
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      id: initialData?.id || Math.random().toString(36).substr(2, 9),
      name: name.trim(),
      limit: parseCurrencyToNumber(limit),
      currentBill: initialData?.currentBill || 0,
      closingDate: parseInt(closingDate) || 1,
      dueDate: parseInt(dueDate) || 1
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-[110] p-0 md:p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-t-[2.5rem] md:rounded-[2.5rem] p-7 md:p-10 shadow-2xl animate-in slide-in-from-bottom border-t border-slate-100 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-xl font-black text-slate-900 dark:text-slate-50 tracking-tight">{initialData ? 'Editar Cartão' : 'Novo Cartão'}</h2>
          <button 
            type="button"
            onClick={onClose} 
            className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-400 flex items-center justify-center hover:text-slate-600 transition-colors shadow-sm"
          >
            <i className="fa-solid fa-times text-sm"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nome do Cartão</label>
            <input 
              required 
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-5 text-sm font-bold text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-600/10 placeholder:opacity-30" 
              placeholder="Ex: Nubank, Inter, XP..."
            />
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Limite Total</label>
            <input 
              required 
              type="text"
              inputMode="decimal"
              value={limit}
              onChange={(e) => setLimit(maskCurrency(e.target.value))}
              className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-5 text-sm font-bold text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-600/10" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Dia Fechamento</label>
              <input 
                required 
                type="number"
                min="1"
                max="31"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-5 text-sm font-bold text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-600/10" 
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Dia Vencimento</label>
              <input 
                required 
                type="number"
                min="1"
                max="31"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-5 text-sm font-bold text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-600/10" 
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full bg-blue-600 text-white py-5 rounded-[1.5rem] font-black text-sm uppercase tracking-widest shadow-xl shadow-blue-600/20 active:scale-[0.97] transition-all mt-4 hover:bg-blue-700 flex items-center justify-center gap-3"
          >
            <i className="fa-solid fa-check-circle"></i>
            {initialData ? 'Salvar Alterações' : 'Cadastrar Cartão'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CardModal;
